import React, { useMemo, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/template-api';

import { useAbortController } from '../../../../hooks/useAbortController';

import { generateUUID } from '../../../../helpers/generateUUID';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import StepCard from '../StepCard/StepCard';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import StepEditorModal from '../StepEditorModal/StepEditorModal';
import DeleteStepModal from '../DeleteStepModal/DeleteStepModal';
import { Button } from '../../../../design-system';

const StepsSection = ({
    playbookTemplateId,
    steps,
    icon_color,
    isEditable,
    setPlaybookTemplate,
}) => {
    const sortedSteps = useMemo(() => [...steps].sort((a, b) => a.order - b.order), [steps]);

    const { abortRequest, getSignal, isAbortError } = useAbortController();

    const [actionModal, setActionModal] = useState({ action: null });
    const closeActionModal = () => setActionModal({ action: null });

    const [errorAlert, setErrorAlert] = useState(null);

    const setSteps = (cb) => {
        setPlaybookTemplate((template) => ({
            ...template,
            default_version: {
                ...template.default_version,
                steps: cb(template.default_version.steps || []),
            },
        }));
    };

    const saveChangedOrder = async () => {
        try {
            try {
                abortRequest();
                const signal = getSignal();

                const requestBody = { steps: steps.map(({ temporaryId, ...step }) => step) };
                await client.patch(
                    API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
                    requestBody,
                    { signal }
                );
            } catch (e) {
                if (isAbortError(e)) return;

                setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });

                const { data } = await client.get(
                    API.ROUTES.template.playbookTemplate + playbookTemplateId + '/'
                );
                const steps = data.default_version?.steps;
                const formattedSteps = (steps || [])
                    .map((step) => ({
                        ...step,
                        temporaryId: generateUUID(),
                    }))
                    .sort((a, b) => a.order - b.order);
                setSteps(() => formattedSteps);
            }
        } catch (e) {}
    };

    const areSteps = !!steps.length;

    return (
        <div className="flex flex-col gap-3">
            {areSteps &&
                sortedSteps.map((step) => (
                    <StepCard
                        key={step.temporaryId}
                        step={step}
                        icon_color={icon_color}
                        isEditable={isEditable}
                        setSteps={setSteps}
                        handleDrop={saveChangedOrder}
                        setActionModal={setActionModal}
                    />
                ))}

            {!areSteps && (
                <p className="font-body-bold text-body-bold-s text-neutral-500">No Steps found</p>
            )}

            {isEditable && (
                <div className="flex justify-end">
                    <Button
                        type="link"
                        size="xs"
                        text="Add Step"
                        underlined
                        onClick={() => setActionModal({ action: 'create' })}
                    />
                </div>
            )}

            {(actionModal.action === 'create' || actionModal.action === 'edit') && (
                <StepEditorModal
                    mode={actionModal.action}
                    currentStep={actionModal.step}
                    allSteps={sortedSteps}
                    setSteps={setSteps}
                    playbookTemplateId={playbookTemplateId}
                    onClose={closeActionModal}
                />
            )}

            {actionModal.action === 'delete' && (
                <DeleteStepModal
                    name={actionModal.step?.name}
                    order={actionModal.step?.order}
                    temporaryId={actionModal.step?.temporaryId}
                    allSteps={sortedSteps}
                    setSteps={setSteps}
                    playbookTemplateId={playbookTemplateId}
                    onClose={closeActionModal}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default StepsSection;
